import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ChevronRight, ChevronLeft, ChevronDown, Lock, Globe, Loader2,
  CheckCircle2, AlertCircle, Plus, Building2, RefreshCw, ExternalLink, Search,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const GithubIcon = ({ size = 16, color = 'currentColor', style }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} style={style} aria-hidden="true">
    <path d="M12 .5C5.65.5.5 5.65.5 12c0 5.08 3.29 9.38 7.86 10.9.58.1.79-.25.79-.55v-2.1c-3.2.69-3.87-1.36-3.87-1.36-.52-1.33-1.28-1.68-1.28-1.68-1.05-.72.08-.7.08-.7 1.16.08 1.77 1.19 1.77 1.19 1.03 1.76 2.7 1.25 3.36.95.1-.74.4-1.25.73-1.54-2.55-.29-5.24-1.28-5.24-5.68 0-1.25.45-2.28 1.18-3.08-.12-.29-.51-1.46.11-3.05 0 0 .96-.31 3.15 1.18.91-.25 1.89-.38 2.87-.38s1.96.13 2.87.38c2.19-1.49 3.15-1.18 3.15-1.18.62 1.59.23 2.76.11 3.05.73.8 1.18 1.83 1.18 3.08 0 4.41-2.69 5.39-5.25 5.67.41.36.78 1.07.78 2.15v3.19c0 .3.21.66.79.55A11.5 11.5 0 0 0 23.5 12C23.5 5.65 18.35.5 12 .5z"/>
  </svg>
);

export default function GitHubConnect({ onRepoSelected, importStatus, onNext, onBack }) {
  const [connected, setConnected] = useState(null);
  const [username, setUsername] = useState('');
  const [installations, setInstallations] = useState([]);
  const [activeInstallationId, setActiveInstallationId] = useState(null);
  const [orgDropdownOpen, setOrgDropdownOpen] = useState(false);

  const [repos, setRepos] = useState([]);
  const [loadingRepos, setLoadingRepos] = useState(false);
  const [reposError, setReposError] = useState('');
  const [search, setSearch] = useState('');
  const [importing, setImporting] = useState(null);
  const [importError, setImportError] = useState('');

  const loadRepos = useCallback(async () => {
    setLoadingRepos(true);
    setReposError('');
    try {
      const r = await fetch(`${API}/api/github/repos`, { credentials: 'include' });
      if (r.status === 409) {
        setConnected(false);
        setUsername('');
        setRepos([]);
        setReposError('Your GitHub App installation was removed. Please reconnect.');
        return;
      }
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to list repos');
      }
      const data = await r.json();
      data.sort((a, b) => (b.updated_at || '').localeCompare(a.updated_at || ''));
      setRepos(data);
    } catch (e) {
      setReposError(e.message);
    } finally {
      setLoadingRepos(false);
    }
  }, []);

  const loadInstallations = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/github/installations`, { credentials: 'include' });
      if (r.ok) {
        const d = await r.json();
        setInstallations(d.installations || []);
        setActiveInstallationId(d.active_id || null);
      }
    } catch {}
  }, []);

  const checkStatus = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/github/status`, { credentials: 'include' });
      if (r.ok) {
        const data = await r.json();
        setConnected(!!data.connected);
        setUsername(data.username || '');
        setActiveInstallationId(data.installation_id || null);
        if (data.connected) {
          loadInstallations();
          loadRepos();
        }
      } else {
        setConnected(false);
      }
    } catch {
      setConnected(false);
    }
  }, [loadRepos, loadInstallations]);

  useEffect(() => {
    checkStatus();
    const params = new URLSearchParams(window.location.search);
    if (params.get('github_connected') === 'true') {
      window.history.replaceState({}, '', window.location.pathname);
      setTimeout(checkStatus, 500);
    }
    if (params.get('github_error')) {
      setReposError(`GitHub error: ${params.get('github_error')}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [checkStatus]);

  // Auto-refresh when window regains focus (returning from GitHub install/manage)
  useEffect(() => {
    if (!connected) return undefined;
    const onFocus = () => { loadInstallations(); loadRepos(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [connected, loadInstallations, loadRepos]);

  const handleConnect = async () => {
    try {
      const r = await fetch(`${API}/api/github/connect`, { credentials: 'include' });
      if (!r.ok) throw new Error('Failed to initiate GitHub connect');
      const data = await r.json();
      window.location.href = data.redirect_url;
    } catch (e) {
      setReposError(e.message);
    }
  };

  const handleAddOrg = async () => {
    try {
      const r = await fetch(`${API}/api/github/install-new-org-url`, { credentials: 'include' });
      if (r.ok) {
        const d = await r.json();
        window.open(d.url, '_blank');
      }
    } catch {}
  };

  const handleSwitchInstallation = async (instId) => {
    setOrgDropdownOpen(false);
    if (instId === activeInstallationId) return;
    try {
      await fetch(`${API}/api/github/installations/${instId}/activate`, {
        method: 'POST', credentials: 'include',
      });
      setActiveInstallationId(instId);
      await loadRepos();
    } catch {}
  };

  const handleImport = async (repo) => {
    setImporting(repo.full_name);
    setImportError('');
    try {
      const r = await fetch(`${API}/api/github/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ repo_full_name: repo.full_name, clone_url: repo.clone_url }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Import failed');
      }
      const data = await r.json();
      onRepoSelected && onRepoSelected(data);
      setTimeout(() => { onNext && onNext(); }, 900);
    } catch (e) {
      setImportError(e.message);
      setImporting(null);
    }
  };

  const activeOrg = useMemo(
    () => installations.find(i => i.id === activeInstallationId) || null,
    [installations, activeInstallationId],
  );

  const btnGhost = {
    padding: '8px 12px', background: 'transparent',
    border: '1px solid rgba(255,255,255,0.1)',
    color: 'rgba(255,255,255,0.6)', fontSize: 11, fontWeight: 600,
    cursor: 'pointer', fontFamily: 'inherit',
    display: 'inline-flex', alignItems: 'center', gap: 5,
  };

  // ---------- Loading status ----------
  if (connected === null) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0', color: 'rgba(255,255,255,0.5)' }} data-testid="gh-checking">
        <Loader2 size={22} style={{ animation: 'spin 1s linear infinite' }} />
        <div style={{ fontSize: 12, marginTop: 10 }}>Checking GitHub connection...</div>
      </div>
    );
  }

  // ---------- Not connected ----------
  if (!connected) {
    return (
      <div data-testid="gh-not-connected">
        <div style={{
          padding: 32, background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.06)', textAlign: 'center',
        }}>
          <GithubIcon size={40} color="#fff" style={{ margin: '0 auto 14px', display: 'block' }} />
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6, color: '#F5F5F7' }}>Connect your GitHub account</div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 22, lineHeight: 1.5 }}>
            Install the BornToBeAlive Studio GitHub App to import<br />your public and private repositories.
          </div>
          <button
            onClick={handleConnect}
            data-testid="gh-connect-btn"
            style={{
              padding: '12px 28px', background: '#7C3AED', color: '#000',
              border: 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer',
              fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 8,
            }}
            onMouseEnter={e => e.currentTarget.style.boxShadow = '0 0 30px rgba(124,58,237,0.4)'}
            onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
          >
            <GithubIcon size={15} /> Connect GitHub
          </button>
          {reposError && (
            <div style={{ marginTop: 16, fontSize: 11, color: '#EF4444', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              <AlertCircle size={12} /> {reposError}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
          <button onClick={onBack} style={btnGhost} data-testid="gh-back-btn">
            <ChevronLeft size={14} /> Back
          </button>
        </div>
      </div>
    );
  }

  // ---------- Connected: org dropdown + repos list ----------
  const filtered = repos.filter(r =>
    !search.trim() || r.full_name.toLowerCase().includes(search.toLowerCase().trim())
  );

  return (
    <div data-testid="gh-connected">

      {/* Connection chip */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
        background: 'rgba(124,58,237,0.08)', border: '1px solid rgba(124,58,237,0.2)',
        marginBottom: 18,
      }}>
        <CheckCircle2 size={13} color="#7C3AED" />
        <div style={{ flex: 1, fontSize: 12 }}>
          Signed in as <strong style={{ color: '#7C3AED' }}>{username || 'GitHub user'}</strong>
        </div>
        <button onClick={() => { loadInstallations(); loadRepos(); }}
                data-testid="gh-refresh-btn"
                title="Refresh"
                style={btnGhost}>
          <RefreshCw size={11} /> Refresh
        </button>
      </div>

      {/* Organization picker */}
      <div style={{ marginBottom: 18 }}>
        <div style={{
          fontSize: 9, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase',
          color: 'rgba(255,255,255,0.45)', marginBottom: 8,
          display: 'flex', alignItems: 'center', gap: 7,
        }}>
          <Building2 size={11} /> Connected organizations · {installations.length}
        </div>
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setOrgDropdownOpen(!orgDropdownOpen)}
            data-testid="gh-org-picker"
            style={{
              width: '100%', padding: '12px 14px',
              display: 'flex', alignItems: 'center', gap: 12,
              background: 'rgba(255,255,255,0.03)',
              border: `1px solid ${orgDropdownOpen ? 'rgba(124,58,237,0.4)' : 'rgba(255,255,255,0.08)'}`,
              color: '#fff', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
              transition: 'all 0.15s',
            }}
          >
            {activeOrg?.account_avatar_url ? (
              <img src={activeOrg.account_avatar_url} alt="" style={{ width: 26, height: 26, borderRadius: 4, flexShrink: 0 }} />
            ) : (
              <div style={{
                width: 26, height: 26, flexShrink: 0,
                background: 'rgba(124,58,237,0.15)',
                border: '1px solid rgba(124,58,237,0.3)',
                color: '#7C3AED',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Building2 size={12} />
              </div>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {activeOrg?.account_login || 'Select an organization'}
              </div>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 2 }}>
                {activeOrg?.account_type || ''}
              </div>
            </div>
            <span style={{
              fontSize: 9, fontWeight: 700, padding: '3px 7px',
              background: 'rgba(124,58,237,0.15)', color: '#7C3AED',
              border: '1px solid rgba(124,58,237,0.3)',
              letterSpacing: '0.06em', textTransform: 'uppercase',
              display: 'inline-flex', alignItems: 'center', gap: 4,
            }}>
              <span style={{ width: 5, height: 5, background: '#7C3AED', borderRadius: '50%' }} />
              Connected
            </span>
            <ChevronDown
              size={14}
              color="rgba(255,255,255,0.4)"
              style={{ transform: orgDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}
            />
          </button>

          {orgDropdownOpen && (
            <>
              <div onClick={() => setOrgDropdownOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4,
                background: '#0F0F1A',
                border: '1px solid rgba(255,255,255,0.1)',
                boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
                zIndex: 50, maxHeight: 320, overflowY: 'auto',
              }}>
                {installations.map(inst => {
                  const isActive = inst.id === activeInstallationId;
                  return (
                    <button
                      key={inst.id}
                      onClick={() => handleSwitchInstallation(inst.id)}
                      data-testid={`gh-org-option-${inst.account_login}`}
                      style={{
                        width: '100%', padding: '10px 14px',
                        display: 'flex', alignItems: 'center', gap: 10,
                        background: isActive ? 'rgba(124,58,237,0.08)' : 'transparent',
                        border: 'none',
                        borderBottom: '1px solid rgba(255,255,255,0.04)',
                        color: '#fff', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
                      }}
                      onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
                      onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                    >
                      {inst.account_avatar_url ? (
                        <img src={inst.account_avatar_url} alt="" style={{ width: 22, height: 22, borderRadius: 4, flexShrink: 0 }} />
                      ) : (
                        <div style={{
                          width: 22, height: 22, flexShrink: 0,
                          background: 'rgba(255,255,255,0.06)',
                          color: 'rgba(255,255,255,0.6)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <Building2 size={11} />
                        </div>
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#fff' }}>
                          {inst.account_login}
                        </div>
                        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>
                          {inst.account_type}
                        </div>
                      </div>
                      {isActive && <CheckCircle2 size={13} color="#7C3AED" />}
                    </button>
                  );
                })}
                <button
                  onClick={() => { setOrgDropdownOpen(false); handleAddOrg(); }}
                  data-testid="gh-add-org"
                  style={{
                    width: '100%', padding: '12px 14px',
                    display: 'flex', alignItems: 'center', gap: 8,
                    background: 'transparent', border: 'none',
                    color: '#7C3AED', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
                    fontSize: 12, fontWeight: 700,
                    borderTop: '1px solid rgba(255,255,255,0.06)',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(124,58,237,0.06)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <Plus size={12} /> Add another GitHub organization
                  <ExternalLink size={10} style={{ marginLeft: 'auto', opacity: 0.7 }} />
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Repository selector */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8,
      }}>
        <div style={{
          fontSize: 9, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase',
          color: 'rgba(255,255,255,0.45)',
          display: 'flex', alignItems: 'center', gap: 7, flex: 1,
        }}>
          <GithubIcon size={11} color="rgba(255,255,255,0.45)" /> Pick a repository · {repos.length}
        </div>
        {activeOrg?.html_url && (
          <a
            href={activeOrg.html_url}
            target="_blank"
            rel="noreferrer"
            data-testid="gh-manage-repos"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              padding: '5px 10px',
              fontSize: 9.5, fontWeight: 700, letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: '#7C3AED',
              background: 'rgba(124,58,237,0.08)',
              border: '1px solid rgba(124,58,237,0.3)',
              borderRadius: 6,
              textDecoration: 'none', fontFamily: 'inherit',
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(124,58,237,0.15)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(124,58,237,0.08)'; }}
            title="Open GitHub to add or remove repositories the App can access. Come back here when done."
          >
            <Plus size={10} /> Add / remove repos <ExternalLink size={9} />
          </a>
        )}
      </div>

      {/* Helper line — visible when at least one repo loaded, so the user knows
          the list is what the App currently sees, and how to extend it. */}
      {activeOrg?.html_url && !loadingRepos && !reposError && repos.length > 0 && (
        <div style={{
          fontSize: 10.5, color: 'rgba(255,255,255,0.42)',
          marginBottom: 10, lineHeight: 1.5,
        }}>
          Don't see a repository? Use <strong style={{ color: 'rgba(255,255,255,0.7)' }}>Add / remove repos</strong> to grant the GitHub App access — the list refreshes when you return.
        </div>
      )}

      {/* Search */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '10px 12px', marginBottom: 10,
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.08)',
      }}>
        <Search size={12} color="rgba(255,255,255,0.4)" />
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search repositories..."
          data-testid="gh-search-input"
          style={{
            flex: 1, background: 'transparent', border: 'none',
            color: '#fff', fontSize: 12, fontFamily: 'inherit', outline: 'none',
          }}
        />
      </div>

      <div style={{
        maxHeight: 360, overflowY: 'auto',
        border: '1px solid rgba(255,255,255,0.06)',
        background: 'rgba(255,255,255,0.02)',
      }} data-testid="gh-repo-list">
        {loadingRepos && (
          <div style={{ padding: 30, textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontSize: 12 }}>
            <Loader2 size={18} style={{ animation: 'spin 1s linear infinite', marginBottom: 8 }} />
            <div>Loading repositories...</div>
          </div>
        )}
        {!loadingRepos && reposError && (
          <div style={{ padding: 20, textAlign: 'center', color: '#EF4444', fontSize: 12 }}>
            <AlertCircle size={16} style={{ marginBottom: 6 }} />
            <div>{reposError}</div>
            <button onClick={loadRepos} style={{ ...btnGhost, marginTop: 10, fontSize: 10 }}>Retry</button>
          </div>
        )}
        {!loadingRepos && !reposError && filtered.length === 0 && (
          <div style={{ padding: 26, textAlign: 'center', color: 'rgba(255,255,255,0.5)', fontSize: 11, lineHeight: 1.6 }}>
            {repos.length === 0 ? (
              <>
                <Building2 size={20} color="rgba(255,255,255,0.3)" style={{ margin: '0 auto 10px', display: 'block' }} />
                <div style={{ fontSize: 12, fontWeight: 700, color: '#fff', marginBottom: 4 }}>
                  No repositories yet on this organization
                </div>
                <div style={{ marginBottom: 14 }}>
                  Grant the GitHub App access to a repo, then come back here.
                </div>
                <button onClick={handleAddOrg} data-testid="gh-grant-access-btn" style={{
                  ...btnGhost, color: '#7C3AED', borderColor: 'rgba(124,58,237,0.3)',
                  fontSize: 11, padding: '8px 14px',
                }}>
                  <Plus size={11} /> Grant access on GitHub <ExternalLink size={9} />
                </button>
              </>
            ) : 'No repositories match your search.'}
          </div>
        )}
        {!loadingRepos && filtered.map(repo => {
          const isImporting = importing === repo.full_name;
          const isImported = importStatus?.repo_full_name === repo.full_name && importStatus?.project_id;
          return (
            <button
              key={repo.id}
              onClick={() => !importing && handleImport(repo)}
              disabled={!!importing}
              data-testid={`gh-repo-${repo.name}`}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                width: '100%', padding: '12px 14px', textAlign: 'left',
                background: isImported ? 'rgba(124,58,237,0.08)' : 'transparent',
                border: 'none',
                borderBottom: '1px solid rgba(255,255,255,0.04)',
                color: '#fff', cursor: importing ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit', opacity: importing && !isImporting ? 0.4 : 1,
                transition: 'background 0.15s',
              }}
              onMouseEnter={e => { if (!importing) e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
              onMouseLeave={e => { if (!importing) e.currentTarget.style.background = isImported ? 'rgba(124,58,237,0.08)' : 'transparent'; }}
            >
              {repo.private
                ? <Lock size={13} color="rgba(255,255,255,0.5)" />
                : <Globe size={13} color="rgba(255,255,255,0.5)" />}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {repo.full_name}
                </div>
                {repo.description && (
                  <div style={{
                    fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 2,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    {repo.description}
                  </div>
                )}
              </div>
              {repo.default_branch && (
                <span style={{
                  fontSize: 9, padding: '2px 6px', fontWeight: 700,
                  background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.55)',
                  border: '1px solid rgba(255,255,255,0.06)',
                  fontFamily: "'JetBrains Mono', monospace",
                }}>
                  {repo.default_branch}
                </span>
              )}
              {repo.language && (
                <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', flexShrink: 0 }}>
                  {repo.language}
                </span>
              )}
              {isImporting
                ? <Loader2 size={14} color="#7C3AED" style={{ animation: 'spin 1s linear infinite' }} />
                : isImported
                  ? <CheckCircle2 size={14} color="#7C3AED" />
                  : <ChevronRight size={14} color="rgba(255,255,255,0.3)" />}
            </button>
          );
        })}
      </div>

      {importError && (
        <div style={{
          marginTop: 12, padding: '10px 14px', background: 'rgba(239,68,68,0.08)',
          border: '1px solid rgba(239,68,68,0.2)', fontSize: 11, color: '#EF4444',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <AlertCircle size={12} /> {importError}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
        <button onClick={onBack} style={btnGhost} disabled={!!importing} data-testid="gh-back-btn">
          <ChevronLeft size={14} /> Back
        </button>
        {importStatus?.project_id && (
          <button
            onClick={onNext}
            data-testid="gh-next-btn"
            style={{
              padding: '10px 20px', background: '#7C3AED', color: '#000', border: 'none',
              fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}
          >
            Continue <ChevronRight size={14} />
          </button>
        )}
      </div>
    </div>
  );
}
